import { DateTime } from 'luxon';
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import { ApiPlaneraResaService } from '../services/api-planera-resa/api-planera-resa.service';
import { type IErrorResponse } from '../models/error-response.model';
import { ErrorHandlerHelper } from '../helpers/error-handler.helper';

export enum Status {
  notperformed = 'notperformed',
  obsolete = 'obsolete',
  loading = 'loading',
  success = 'success',
  failed = 'failed',
}

export enum DataSource {
  backend = 'backend',
  apiPlaneraResa = 'apiplaneraresa',
}

type DependencyList = readonly unknown[];

type FetchState<T> = {
  status: Status;
  updated: DateTime;
  data: T | undefined;
  httpResponseStatusCode: number | undefined;
  errorResponse?: IErrorResponse;
};

export class FetchConfig {
  public dataSource: DataSource = DataSource.backend;
  public keepLastStateIfPrevented: boolean = false;
  public delayInMs: number = 300;
  public static default: FetchConfig = {
    dataSource: DataSource.backend,
    keepLastStateIfPrevented: false,
    delayInMs: 300,
  };
}

export default function useFetch<T>(
  url: string | undefined,
  preventFetch: boolean = false,
  delayFetch: boolean = false, // Used primarily for autocomplete functionality
  config: FetchConfig = FetchConfig.default,
  deps: DependencyList = [],
): [FetchState<T>, Dispatch<SetStateAction<FetchState<T>>>] {
  const [requestState, setRequestState] = useState<FetchState<T>>({
    status: Status.notperformed,
    updated: DateTime.now(),
    data: undefined,
    httpResponseStatusCode: undefined,
  });
  const searchTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    if (preventFetch) {
      if (!config.keepLastStateIfPrevented) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setRequestState({ status: Status.notperformed, updated: DateTime.now(), data: undefined, httpResponseStatusCode: undefined });
      }
      return () => {};
    }

    // See https://stackoverflow.com/questions/56800694/what-is-the-expected-return-of-useeffect-used-for for more information about this "ignore pattern"
    let ignore = false;
    setRequestState({ status: Status.loading, updated: DateTime.now(), data: undefined, httpResponseStatusCode: undefined });

    async function fetchData(): Promise<void> {
      let response: Response | undefined;
      try {
        let json: T | undefined;
        if (config.dataSource === DataSource.apiPlaneraResa) {
          json = (await ApiPlaneraResaService.get(url || '')) as T;
        } else {
          response = await fetch(url || '', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!response.ok) throw new Error(response.statusText);
          json = await response.json();
        }
        if (!ignore) {
          setRequestState({ status: Status.success, updated: DateTime.now(), data: json, httpResponseStatusCode: response?.status });
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (error: any) {
        if (!ignore) {
          setRequestState({
            status: Status.failed,
            updated: DateTime.now(),
            data: undefined,
            httpResponseStatusCode: response?.status,
            errorResponse: ErrorHandlerHelper.mapErrorToIErrorResponse(error),
          });
        }
      }
    }

    if (!delayFetch) {
      fetchData();
    } else {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
        searchTimer.current = undefined;
      }
      searchTimer.current = setTimeout(() => {
        fetchData();
      }, config.delayInMs);
    }

    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, preventFetch, delayFetch, config.keepLastStateIfPrevented, config.dataSource, config.delayInMs, ...deps]);

  return [requestState, setRequestState];
}
